import React, { useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Icon, Image } from 'components';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import { Button, Form } from 'components/form';
import { Col, Row } from 'components/layout';

import { API_ROUTES } from 'lib/api-routes';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { TUserApiResponse } from 'lib/models/User.model';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';
import { parseErrorResponse } from 'lib/utils/parser';
import { convertBase64 } from 'utils/format';

import Footer from '../Footer';
import Header from '../Header';
import StepHeading from '../StepHeading';
import styles from './Step3.module.scss';

interface Step3Props {
  progress: number;
  setStep: (step: number) => void;
}
interface UserAttributes {
  first_name: string;
  last_name: string;
  role: string;
  avatar: { file: string };
}

export const Step3: React.FC<Step3Props> = ({ progress, setStep }) => {
  const { updateUserApiResponse, recruiterCompany, recruiter } = useAuth();

  const [profilePictureRemoved, setProfilePictureRemoved] =
    React.useState<boolean>(false);

  const [t] = useTranslation('signup.recruiter');
  const isNewCompany = useMemo(() => {
    return recruiterCompany?.authenticated
      ? !(
          recruiterCompany?.industry?.data?.id &&
          recruiterCompany?.icon_url &&
          recruiterCompany?.description &&
          recruiterCompany?.short_description &&
          recruiterCompany?.employees_count
        )
      : true;
  }, [recruiterCompany]);

  const {
    first_name: firstName,
    last_name: lastName,
    role: roleValue,
  } = recruiter || {};

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    defaultValues: {
      first_name: firstName,
      last_name: lastName,
      role: roleValue,
      avatar: { file: '' },
    },
  });

  const [showError, setShowError] = React.useState<string>('');

  const avatar = watch('avatar');

  const onSubmit: SubmitHandler<UserAttributes> = async (user) => {
    const { avatar, ...rest } = user;
    const body = {
      recruiter: {
        ...rest,
        ...(avatar?.file && { avatar: { file: avatar.file.split(',')[1] } }),
        flow_state: isNewCompany
          ? ONBOARDING_FLOW_STATE.PRE_4
          : ONBOARDING_FLOW_STATE.PRE_COMPLETE,
      },
    };

    try {
      const { data: response } = await apiInstance.patch<TUserApiResponse>(
        API_ROUTES.RECRUITER_SIGNUP,
        body
      );

      updateUserApiResponse(response);
      const step = isNewCompany ? 4 : 6;
      setStep(step);
    } catch (error) {
      const message = parseErrorResponse(error);
      setShowError(message);
    }
  };

  const uploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    const base64 = (await convertBase64(file as Blob)) as string;
    setValue('avatar', { file: base64 });
  };

  return (
    <>
      <Header percentComplete={progress} />
      <div className={styles.stepContent}>
        <StepHeading
          title={t('step-3.header.title')}
          desc={t('step-3.header.desc')}
        />

        <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Row className={styles.mb24}>
            <Col span={24}>
              <InputV2
                label={t('step-3.first-name.label')}
                {...register('first_name', {
                  required: t('step-3.first-name.required'),
                })}
                placeholder={t('step-3.first-name.placeholder')}
                isInvalid={!!errors.first_name}
                labelClassName={styles.fieldLabel}
              />
              {errors.first_name?.message && (
                <div className={styles.errorMessage}>
                  {errors.first_name?.message}
                </div>
              )}
            </Col>
          </Row>
          <Row className={styles.mb24}>
            <Col span={24}>
              <InputV2
                label={t('step-3.last-name.label')}
                {...register('last_name', {
                  required: t('step-3.last-name.required'),
                })}
                placeholder={t('step-3.last-name.placeholder')}
                isInvalid={!!errors.last_name}
                labelClassName={styles.fieldLabel}
              />
              {errors.last_name?.message && (
                <div className={styles.errorMessage}>
                  {errors.last_name?.message}
                </div>
              )}
            </Col>
          </Row>
          <Row className={styles.mb24}>
            <Col span={24}>
              <InputV2
                label={t('step-3.position.label')}
                {...register('role', {
                  required: t('step-3.position.required'),
                })}
                placeholder={t('step-3.position.placeholder')}
                isInvalid={!!errors.role}
                labelClassName={styles.fieldLabel}
              />
              {errors.role?.message && (
                <div className={styles.errorMessage}>
                  {errors.role?.message}
                </div>
              )}
            </Col>
          </Row>

          <Row className={styles.imageUploader}>
            <Col span={24}>
              <div className={styles.fieldLabel}>
                <span>{t('step-3.photo.label')}</span>
                <span className={styles.optional}>(Optional)</span>
              </div>
              <input
                type="file"
                accept="image/*"
                id={`upload-profile-image`}
                hidden
                onChange={uploadImage}
              />
              {avatar?.file ||
              (!profilePictureRemoved && recruiter?.avatar_url) ? (
                <div className={styles.uploadedFile}>
                  <div className={styles.avatarWrapper}>
                    <Image
                      src={avatar?.file || recruiter?.avatar_url || ''}
                      className={styles.image}
                    />
                  </div>

                  <label
                    htmlFor={`upload-profile-image`}
                    className={styles.changePhoto}
                  >
                    <Icon iconName="edit-v2" />
                    <div>{t('step-3.photo.change')}</div>
                  </label>

                  <Button
                    type="button"
                    color="secondary"
                    className={styles.changePhoto}
                    onClick={() => {
                      setProfilePictureRemoved(true);
                      setValue('avatar', { file: '' });
                    }}
                  >
                    <Icon iconName="trash" />
                    {t('step-3.photo.remove')}
                  </Button>
                </div>
              ) : (
                <label
                  htmlFor={`upload-profile-image`}
                  className={styles.mainButton}
                >
                  {t('step-3.photo.upload')}
                </label>
              )}
            </Col>
          </Row>

          {showError && <div className={styles.errorMessage}>{showError}</div>}
          <Footer>
            <ButtonV3
              type="submit"
              isFullWidth
              isLoading={isSubmitting}
              size="xlarge"
            >
              {t('next')}
            </ButtonV3>
          </Footer>
        </Form>
      </div>
    </>
  );
};
